import type { PermissionGroupKey } from '@/api/permissions';
import { Permission, PERMISSION_GROUPS, PERMISSION_METADATA } from '@/api/permissions';
import type {
  CatalogGroup,
  CatalogPermission,
  PermissionAssignment,
  RolePermission,
} from '@/shared/modules/role-board/types';

const isActionable = (
  permission: CatalogPermission,
  selection: PermissionSelectionMap,
  canGrantProtected: boolean,
) =>
  !selection.get(permission.permission)?.locked &&
  isPermissionGrantable(permission.permission, canGrantProtected);

/**
 * What a single permission row is currently set to on the role being edited. A
 * permission absent from the map is off.
 */
export type PermissionSelection = {
  /** Seeded on a system role: the row is on and cannot be changed. */
  locked: boolean;
};

export type PermissionSelectionMap = ReadonlyMap<Permission, PermissionSelection>;

export type PermissionRowState = {
  on: boolean;
  locked: boolean;
};

export const toPermissionRowState = (selection?: PermissionSelection): PermissionRowState => ({
  on: selection !== undefined,
  locked: Boolean(selection?.locked),
});

/**
 * Role rows → picker state. `base` rows come back locked because the backend
 * rejects any write that touches them (`ROLE_BASE_IMMUTABLE`), on locked roles
 * only — a duplicate lands every row as `extra` and is fully editable.
 */
export const toRoleSelection = (permissions: readonly RolePermission[], isLocked: boolean) =>
  new Map<Permission, PermissionSelection>(
    permissions.map((row) => [row.permission, { locked: isLocked && row.source === 'base' }]),
  );

const matchesSearch = (permission: CatalogPermission, needle: string) =>
  permission.label.toLowerCase().includes(needle) ||
  permission.description.toLowerCase().includes(needle) ||
  permission.permission.toLowerCase().includes(needle);

/**
 * Narrows the catalogue to permissions matching `search`, dropping groups that
 * end up empty. A group whose own label matches keeps all of its permissions, so
 * searching "funds" shows the whole Funds section rather than nothing.
 */
export const filterCatalogGroups = (
  groups: readonly CatalogGroup[],
  search: string,
): CatalogGroup[] => {
  const needle = search.trim().toLowerCase();
  if (!needle) return [...groups];

  return groups.reduce<CatalogGroup[]>((matched, group) => {
    if (group.label.toLowerCase().includes(needle)) {
      matched.push(group);

      return matched;
    }

    const permissions = group.permissions.filter((permission) => matchesSearch(permission, needle));
    if (permissions.length) matched.push({ ...group, permissions });

    return matched;
  }, []);
};

/** How many of a group's permissions are currently on — the "4/16" counter. */
export const countSelectedInGroup = (group: CatalogGroup, selection: PermissionSelectionMap) =>
  group.permissions.reduce(
    (total, { permission }) => total + (selection.has(permission) ? 1 : 0),
    0,
  );

export const countSelectedInCatalog = (
  groups: readonly CatalogGroup[],
  selection: PermissionSelectionMap,
) => groups.reduce((total, group) => total + countSelectedInGroup(group, selection), 0);

export type PermissionCounts = {
  total: number;
  selected: number;
};

export const countPermissions = (
  groups: readonly CatalogGroup[],
  selection: PermissionSelectionMap,
): PermissionCounts => ({
  total: groups.reduce((total, group) => total + group.permissions.length, 0),
  selected: countSelectedInCatalog(groups, selection),
});

export const formatPermissionSummary = ({ total, selected }: PermissionCounts) =>
  `${selected} of ${total} granted`;

/**
 * Role administration is a protected permission: managing a role no longer
 * requires holding what it grants, but putting `EntityManageRoles` (or its Gamma
 * twin) onto a role stays reserved for an entity Owner or a
 * `GammaTeamAssignSuperAdmin` holder, which is what the backend enforces.
 */
export const PROTECTED_PERMISSIONS: ReadonlySet<Permission> = new Set([
  Permission.EntityManageRoles,
  Permission.GammaManageRoles,
]);

export const canGrantProtectedPermissions = ({
  isOwner,
  heldPermissions,
}: {
  /** Always false on the Gamma plane, which has no Owner role. */
  isOwner: boolean;
  heldPermissions: ReadonlySet<Permission>;
}): boolean => isOwner || heldPermissions.has(Permission.GammaTeamAssignSuperAdmin);

export const isPermissionGrantable = (permission: Permission, canGrantProtected: boolean) =>
  canGrantProtected || !PROTECTED_PERMISSIONS.has(permission);

// ---------------------------------------------------------------------------
// Bulk group actions — "Enable all" and "Clear all"
// (`2026-07-31-permissions-role-board-fe-prompt.md`, "Bulk actions per group").
// ---------------------------------------------------------------------------

export const actionableGroupPermissions = (
  group: CatalogGroup,
  selection: PermissionSelectionMap,
  canGrantProtected: boolean,
) =>
  group.permissions.filter((permission) => isActionable(permission, selection, canGrantProtected));

/** The rows a group's "Enable all" turns on, sent as one request per permission. */
export const buildEnableAllPatch = (
  group: CatalogGroup,
  selection: PermissionSelectionMap,
  canGrantProtected: boolean,
): PermissionAssignment[] =>
  actionableGroupPermissions(group, selection, canGrantProtected)
    .filter((permission) => !toPermissionRowState(selection.get(permission.permission)).on)
    .map((permission) => ({ permission: permission.permission }));

/** Unlike the add path this ignores `canGrantProtected`: removal is never gated. */
export const buildClearAllPatch = (
  group: CatalogGroup,
  selection: PermissionSelectionMap,
): Permission[] =>
  group.permissions
    .filter(({ permission }) => {
      const row = selection.get(permission);

      return row !== undefined && !row.locked;
    })
    .map(({ permission }) => permission);

export const countEnableAllChanges = (
  group: CatalogGroup,
  selection: PermissionSelectionMap,
  canGrantProtected: boolean,
) => buildEnableAllPatch(group, selection, canGrantProtected).length;

export const countClearAllChanges = (group: CatalogGroup, selection: PermissionSelectionMap) =>
  buildClearAllPatch(group, selection).length;

export const BYPASS_GROUP_KEYS: ReadonlySet<PermissionGroupKey> = new Set(['approval-bypass']);

export const isBypassPermission = (permission: Permission) => {
  const group = PERMISSION_METADATA[permission]?.group;

  return group !== undefined && BYPASS_GROUP_KEYS.has(group);
};

export const toCatalogPermission = (permission: Permission): CatalogPermission => {
  const metadata = PERMISSION_METADATA[permission];

  if (!metadata) {
    return { permission, label: permission, description: '', sensitive: true };
  }

  return {
    permission,
    label: metadata.label,
    description: metadata.description,
    sensitive: metadata.sensitive,
  };
};

type CatalogGroupKey = PermissionGroupKey | 'other';

const groupKeyOf = (permission: Permission): CatalogGroupKey =>
  PERMISSION_METADATA[permission]?.group ?? 'other';

const orderOf = (key: string) => PERMISSION_GROUPS[key as PermissionGroupKey]?.order ?? 999;

/**
 * Widens the catalogue with permissions a role already carries but the
 * catalogue does not offer, so they stay visible — and removable — rather than
 * silently held.
 */
export const augmentCatalogGroups = (
  groups: readonly CatalogGroup[],
  present: ReadonlySet<Permission>,
): CatalogGroup[] => {
  const byKey = new Map<string, { label: string; permissions: Map<Permission, CatalogPermission> }>(
    groups.map((group) => [
      group.key,
      {
        label: group.label,
        permissions: new Map(group.permissions.map((entry) => [entry.permission, entry])),
      },
    ]),
  );

  present.forEach((permission) => {
    const key = groupKeyOf(permission);
    const group = byKey.get(key) ?? {
      label: PERMISSION_GROUPS[key as PermissionGroupKey]?.label ?? 'Other',
      permissions: new Map<Permission, CatalogPermission>(),
    };

    if (!group.permissions.has(permission)) {
      group.permissions.set(permission, toCatalogPermission(permission));
    }

    byKey.set(key, group);
  });

  return [...byKey.entries()]
    .sort(([a], [b]) => orderOf(a) - orderOf(b))
    .map(([key, group]) => ({
      key,
      label: group.label,
      permissions: [...group.permissions.values()],
    }));
};
